// ProjectDetail.js
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  CheckCircle,
  RadioButtonUnchecked,
  LocationOn,
  CalendarToday,
  Euro,
  Person,
  Phone,
  Description,
  Article,
  DescriptionOutlined,
  OpenInNew,
  AttachFile,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import {
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  INSTALLATION_TYPE_LABELS,
  PROGRESS_STEPS,
} from "../../utils/models/projectModel";

const formatAddress = (addr) => {
  if (!addr || !addr.street) return "—";
  const parts = [
    addr.street,
    addr.city,
    addr.province,
    addr.zipCode,
    addr.country,
  ].filter(Boolean);
  return parts.join(", ");
};

const ProjectDetail = ({ projectId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { document: project, loading } = useDocument("projects", projectId);
  const { data: salesInvoices } = useCollection("salesInvoices");
  const { data: purchaseInvoices } = useCollection("purchaseInvoices");

  if (loading) return <LoadingSpinner />;
  if (!project) return <EmptyState title="Proyecto no encontrado" />;

    // ============================================
  // CÁLCULOS FINANCIEROS DEL PROYECTO
  // ============================================

  // --- GASTOS (Facturas de Compra) ---
  const purchaseInvoicesForProject = purchaseInvoices?.filter(
  inv => inv.isActive !== false && 
  inv.status === "paid" && 
  inv.projects?.some(p => p.projectId === projectId)
) || [];

  const gastoBase = purchaseInvoicesForProject.reduce((sum, inv) => {
    const projectAssignment = inv.projects?.find(p => p.projectId === projectId);
    if (!projectAssignment?.amount) return sum;
    
    const proportion = inv.total > 0 ? projectAssignment.amount / inv.total : 0;
    return sum + ((inv.baseAmount || 0) * proportion);
  }, 0);

  const gastoIVA = purchaseInvoicesForProject.reduce((sum, inv) => {
    const projectAssignment = inv.projects?.find(p => p.projectId === projectId);
    if (!projectAssignment?.amount) return sum;
    
    const proportion = inv.total > 0 ? projectAssignment.amount / inv.total : 0;
    return sum + ((inv.taxAmount || 0) * proportion);
  }, 0);

  const gastoTotal = gastoBase + gastoIVA;

  // --- GANANCIAS (Facturas de Venta) ---
  const salesInvoicesForProject = salesInvoices?.filter(
  inv => inv.isActive !== false && 
  inv.status === "paid" && 
  inv.projects?.some(p => p.projectId === projectId)
) || [];

  const gananciaBase = salesInvoicesForProject.reduce((sum, inv) => {
    const projectAssignment = inv.projects?.find(p => p.projectId === projectId);
    if (!projectAssignment?.amount) return sum;
    
    const proportion = inv.total > 0 ? projectAssignment.amount / inv.total : 0;
    return sum + ((inv.baseAmount || 0) * proportion);
  }, 0);

  const gananciaIVA = salesInvoicesForProject.reduce((sum, inv) => {
    const projectAssignment = inv.projects?.find(p => p.projectId === projectId);
    if (!projectAssignment?.amount) return sum;
    
    const proportion = inv.total > 0 ? projectAssignment.amount / inv.total : 0;
    return sum + ((inv.taxAmount || 0) * proportion);
  }, 0);

  const gananciaTotal = gananciaBase + gananciaIVA;

  // --- BENEFICIO ---
  const beneficioBase = gananciaBase - gastoBase;
  const beneficioIVA = gananciaIVA - gastoIVA;
  const beneficioTotal = gananciaTotal - gastoTotal;

  // Total facturado (para mantener compatibilidad con el resto del código)
  const totalInvoiced = project.totalInvoiced || gananciaTotal;
  const checklistItems = [
    { key: "hasFileOpened", label: "Apertura expediente" },
    { key: "hasLicensePermit", label: "Licencia de obra" },
    { key: "materialPurchased", label: "Material comprado" },
    { key: "laborHired", label: "Mano de obra contratada" },
    { key: "materialReceived", label: "Material recibido" },
    { key: "liftingEquipmentRented", label: "Equipo de elevación alquilado" },
    { key: "installed", label: "Instalación realizada" },
    { key: "hasBulletinRequested", label: "Boletín solicitado" },
    { key: "hasBulletinReceived", label: "Boletín recibido" },
    { key: "hasRequirement", label: "Requerimiento" },
  ];

  const getActiveStepIndex = () => {
    let lastCompleted = -1;
    PROGRESS_STEPS.forEach((step, index) => {
      if (project[step.key]) lastCompleted = index;
    });
    return lastCompleted;
  };
  const activeStep = getActiveStepIndex();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate("/projects")}
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            mt: 0.5,
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            {project.name}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}
          >
            <Chip
              label={PROJECT_STATUS_LABELS[project.status]}
              size="small"
              color={
                project.status === PROJECT_STATUS.COMPLETED
                  ? "success"
                  : project.status === PROJECT_STATUS.INSTALLING
                    ? "warning"
                    : "info"
              }
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            />
            <Chip
              label={project.clientName}
              size="small"
              variant="outlined"
              sx={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            />
            {project.installationType && (
              <Chip
                label={INSTALLATION_TYPE_LABELS[project.installationType]}
                size="small"
                variant="outlined"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
            {project.status === PROJECT_STATUS.COMPLETED && (
              <Chip
                icon={<Euro sx={{ fontSize: 14 }} />}
                label={`Total: ${totalInvoiced.toLocaleString("es-ES")}€`}
                size="small"
                color="success"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {project.contractId && (
              <Chip
                label="📋 Ver contrato"
                component={Link}
                to={`/contracts/${project.contractId}`}
                clickable
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      {/* GUÍA DE PROGRESO */}
      <Box sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, md: 2 } }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            mb: { xs: 2, sm: 3 },
            color: theme.palette.primary.main,
            fontSize: { xs: "0.85rem", sm: "1rem" },
          }}
        >
          Guía de progreso
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 0,
          }}
        >
          {PROGRESS_STEPS.map((step, index) => {
            const completed = project[step.key];
            const isActive = index === activeStep + 1 && !completed;
            const iconColor = completed
              ? theme.palette.success.main
              : isActive
                ? theme.palette.warning.main
                : theme.palette.text.disabled;
            return (
              <React.Fragment key={step.key}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    minWidth: { xs: 55, md: 100 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 48, sm: 56, md: 64 },
                      height: { xs: 48, sm: 56, md: 64 },
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: completed
                        ? "rgba(0, 147, 93, 0.2)"
                        : isActive
                          ? "rgba(255, 152, 0, 0.2)"
                          : "rgba(255, 255, 255, 0.08)",
                      border: `3px solid ${iconColor}`,
                      mb: 1.5,
                    }}
                  >
                    {completed ? (
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 36 },
                          color: iconColor,
                        }}
                      />
                    ) : isActive ? (
                      <RadioButtonUnchecked
                        sx={{
                          fontSize: { xs: 28, sm: 32, md: 36 },
                          color: iconColor,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{ fontSize: { xs: 20, md: 24 }, opacity: 0.5 }}
                      >
                        {step.icon}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={completed ? 700 : 400}
                    sx={{
                      color: completed ? "text.primary" : "text.secondary",
                      fontSize: { xs: 9, sm: 10, md: 12 },
                      maxWidth: 80,
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
                {index < PROGRESS_STEPS.length - 1 && (
                  <Box
                    sx={{
                      width: { xs: 20, sm: 30, md: 60 },
                      height: 3,
                      bgcolor: completed
                        ? theme.palette.success.main
                        : theme.palette.divider,
                      mx: { xs: 0.5, md: 1 },
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: -5,
                        right: -2,
                        width: 0,
                        height: 0,
                        borderTop: "6px solid transparent",
                        borderBottom: "6px solid transparent",
                        borderLeft: `10px solid ${completed ? theme.palette.success.main : theme.palette.divider}`,
                      },
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* COLUMNA IZQUIERDA */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: "block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Información general
              </Typography>
              {project.description && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 2,
                    mt: 0.5,
                  }}
                >
                  <Description
                    sx={{
                      fontSize: { xs: 16, sm: 18 },
                      color: theme.palette.primary.main,
                      mt: 0.3,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {project.description}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
              >
                Cliente
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  mt: 0.5,
                }}
              >
                <Person
                  sx={{
                    fontSize: { xs: 16, sm: 18 },
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                >
                  {project.clientName}
                </Typography>
              </Box>
              {project.clientPhone && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Teléfono
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                      mt: 0.5,
                    }}
                  >
                    <Phone
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: theme.palette.success.main,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                    >
                      {project.clientPhone}
                    </Typography>
                  </Box>
                </>
              )}
              {project.budget > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Euro
                    sx={{
                      mr: 0.5,
                      fontSize: { xs: 16, sm: 18 },
                      color: theme.palette.warning.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {project.budget.toLocaleString()} €
                  </Typography>
                </Box>
              )}
              {project.status === PROJECT_STATUS.COMPLETED &&
                totalInvoiced > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: "rgba(0, 147, 93, 0.1)",
                      border: "1px solid",
                      borderColor: theme.palette.success.main,
                    }}
                  >
                    <Euro
                      sx={{
                        mr: 0.5,
                        fontSize: { xs: 16, sm: 18 },
                        color: theme.palette.success.main,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      >
                        Total facturado
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color="success.main"
                        sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                      >
                        {totalInvoiced.toLocaleString("es-ES")} €
                      </Typography>
                    </Box>
                  </Box>
                )}

              {/* ARCHIVOS */}
              {project.files && project.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ 
                      mb: 2,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {project.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              wordBreak: 'break-all',
                              '&:hover': {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            {file.name || file.url}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                }}
              >
                <CalendarToday sx={{ fontSize: { xs: 16, sm: 18 } }} />
                Fechas
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {project.startDate && (
                  <Box sx={{ flex: 1, minWidth: "100px" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    >
                      Inicio
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      {new Date(project.startDate).toLocaleDateString("es-ES")}
                    </Typography>
                  </Box>
                )}
                {project.endDate && (
                  <Box sx={{ flex: 1, minWidth: "100px" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    >
                      Fin
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      {new Date(project.endDate).toLocaleDateString("es-ES")}
                    </Typography>
                  </Box>
                )}
                {project.installationDate && (
                  <Box sx={{ flex: 1, minWidth: "100px" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    >
                      Instalación
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      {new Date(project.installationDate).toLocaleDateString(
                        "es-ES",
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

                    {/* 💰 CARD: FINANZAS DEL PROYECTO */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.primary.main,
                  pb: 1,
                  display: "block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                💰 Finanzas del proyecto
              </Typography>

              {/* Gastos */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                    🔴 Gastos (compras)
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
                    {gastoTotal.toLocaleString("es-ES")} €
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    Base: {gastoBase.toLocaleString("es-ES")} €
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    IVA: {gastoIVA.toLocaleString("es-ES")} €
                  </Typography>
                </Box>
              </Box>

              {/* Ganancias */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                    🟢 Ganancias (ventas)
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}>
                    {gananciaTotal.toLocaleString("es-ES")} €
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, pl: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    Base: {gananciaBase.toLocaleString("es-ES")} €
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    IVA: {gananciaIVA.toLocaleString("es-ES")} €
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Beneficio */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  bgcolor: beneficioBase >= 0 ? "rgba(0, 147, 93, 0.1)" : "rgba(211, 47, 47, 0.1)",
                  border: "1px solid",
                  borderColor: beneficioBase >= 0 ? theme.palette.success.main : theme.palette.error.main,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                      Beneficio (sin IVA)
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      sx={{
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        color: beneficioBase >= 0 ? theme.palette.success.main : theme.palette.error.main,
                      }}
                    >
                      {beneficioBase.toLocaleString("es-ES")} €
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem", color: beneficioBase >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                      {gastoBase > 0 ? ((beneficioBase / gastoBase) * 100).toFixed(1) : "—"}%
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* IVA a compensar */}
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: "rgba(255, 152, 0, 0.06)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  IVA a compensar
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem", color: theme.palette.warning.main }}>
                  {beneficioIVA.toLocaleString("es-ES")} €
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                }}
              >
                <LocationOn
                  sx={{
                    fontSize: { xs: 16, sm: 18 },
                    color: theme.palette.error.main,
                  }}
                />
                Dirección
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 175, 163, 0.1)"
                      : "rgba(0, 95, 101, 0.03)",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  {formatAddress(project.address)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* COLUMNA DERECHA */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, sm: 3 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Documentos
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  p: { xs: 1.5, sm: 2.5 },
                  mb: 2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "#fafafa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    minWidth: 150,
                  }}
                >
                  <Article
                    sx={{
                      fontSize: { xs: 24, sm: 32 },
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                    >
                      Boletín / Proyecto
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    >
                      {project.hasBulletinReceived
                        ? "✅ Recibido"
                        : project.hasBulletinRequested
                          ? "⏳ Solicitado"
                          : "❌ Pendiente"}
                    </Typography>
                  </Box>
                </Box>
                {project.bulletinLink ? (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={project.bulletinLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNew />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    {project.bulletinFileName || "Ver boletín"}
                  </Button>
                ) : (
                  <Chip
                    label="Sin enlace"
                    size="small"
                    variant="outlined"
                    color="default"
                    sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.02)"
                      : "#fafafa",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    minWidth: 150,
                  }}
                >
                  <DescriptionOutlined
                    sx={{
                      fontSize: { xs: 24, sm: 32 },
                      color: theme.palette.warning.main,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                    >
                      Requerimiento
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    >
                      {project.hasRequirement ? "⚠️ Adjunto" : "Pendiente"}
                    </Typography>
                    {project.requirementReminderDate && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="error.light"
                        sx={{
                          mt: 0.5,
                          fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        }}
                      >
                        📅 Recordatorio:{" "}
                        {new Date(
                          project.requirementReminderDate,
                        ).toLocaleDateString("es-ES", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {project.requirementLink ? (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={project.requirementLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="warning"
                    startIcon={<OpenInNew />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    {project.requirementFileName || "Ver requerimiento"}
                  </Button>
                ) : (
                  <Chip
                    label="Sin enlace"
                    size="small"
                    variant="outlined"
                    color="default"
                    sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: { xs: 2, sm: 3 },
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Checklist de proceso
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                {checklistItems.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: { xs: 1, sm: 1.5 },
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: project[item.key]
                        ? theme.palette.success.main
                        : theme.palette.divider,
                      background: project[item.key]
                        ? "rgba(0, 147, 93, 0.15)"
                        : theme.palette.background.paper,
                    }}
                  >
                    {project[item.key] ? (
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 16, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <RadioButtonUnchecked
                        sx={{
                          fontSize: { xs: 16, sm: 20 },
                          color: "text.disabled",
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: project[item.key]
                          ? "text.primary"
                          : "text.secondary",
                        fontWeight: project[item.key] ? 500 : 400,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProjectDetail;